import { type JSX, useEffect, useState } from "react";
import styled from "styled-components";
import { useMutation } from "urql";
import { useAPIAlert } from "../../../shared/components/Alerts";
import SharedAiInput from "../../../shared/components/ai/AiInput";
import AiLoader from "../../../shared/components/ai/AiLoader";
import AiThread from "../../../shared/components/ai/AiThread";
import AiIcon from "../../../shared/components/icons/AiIcon";
import t from "../../../shared/translations";
import Modal from "../../components/Modal";
import { type FragmentType, graphql, useFragment } from "../../gql";
import type { ISubmittedTaskVariant, ITaskVariant } from "../query";
import { continueMutation, startMutation } from "./queries";

const AiAssistModal_AiThreadItem = graphql(`
    fragment AiAssistModal_AiThreadItem on PlayerAiThreadItem {
        id
        type
        role
        content
    }
`);

interface IProps {
    variant: ITaskVariant | ISubmittedTaskVariant;
}

function AiAssistModal({ variant }: IProps): JSX.Element {
    const [messages, setMessages] = useState<Array<FragmentType<typeof AiAssistModal_AiThreadItem>>>([]);
    const [threadId, setThreadId] = useState<string>("");
    const [starting, setStarting] = useState<boolean>(true);
    const [startResult, start] = useMutation(startMutation);
    const [continueResult, continueThread] = useMutation(continueMutation);
    const APIAlert = useAPIAlert();

    const baseVariantId = variant.id;

    useEffect(() => {
        void (async () => {
            setStarting(true);
            setThreadId("");
            setMessages([]);
            const res = await start({ baseVariantId });
            if (APIAlert(res, "player.error.error")) {
                const data = res.data?.taskAiAssistThreadStart?.taskAiAssistThread;
                if (data) {
                    setThreadId(data.id);
                    setMessages(data.aiThreadItems);
                }
            }
            setStarting(false);
        })();
    }, [baseVariantId, start]);

    const onSubmit = async (input: string) => {
        if (!threadId || continueResult.fetching) {
            return;
        }
        const res = await continueThread({ threadId, input });
        if (APIAlert(res, "player.error.error")) {
            const data = res.data?.taskAiAssistThreadContinue?.taskAiAssistThread;
            setThreadId(data.id);
            setMessages(data.aiThreadItems);
        }
    };

    const loading = starting || startResult.fetching || continueResult.fetching;

    const items = useFragment(AiAssistModal_AiThreadItem, messages);

    return (
        <Modal modal="Task/AiAssist">
            <h3>
                <TitleIcon aria-hidden white={false} width={64} height={64} />
                {t("player.task.ai-assist")}
            </h3>
            {starting ? (
                <AiLoader />
            ) : (
                <div>
                    <AiThread messages={items as any} />
                    <SharedAiInput onSubmit={onSubmit} loading={loading} />
                </div>
            )}
        </Modal>
    );
}

const TitleIcon = styled(AiIcon)`
    display: inline-block;
    vertical-align: middle;
    margin-right: 8px;
`;

export default AiAssistModal;
